import React from 'react';
import billingUtils from '../utils/billingUtils';
import { EVENT_IDS } from '../model/ClickstreamEvents';

import { LoadingConstants, PermissionCheck, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import PaymentInfoModal from '../components/modals/PaymentInfoModal';

// containers
import SpinnerContainer from '../components/SpinnerContainer';

// constants
import { INVOICE_PAYMENTS } from '../constants/RouteConstants';
import { Box, Divider, Heading, Icon, Paragraph, Table, Text, TextButton } from '@zillow/constellation-10';
import { IconInfoFilled } from '@zillow/constellation-icons';
import { AuthCheckWrapper, HeadWrapper, useIsScreenDesktop, useRmwPaths } from '@zg-rentals/rental-manager-env';
import { useHistory } from 'react-router-dom';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { useAppMaintenance } from '../hooks/useMaintenance';
import ChangeBillingInfoModal from '../components/modals/ChangeBillingInfoModal';
import { emitEditBillingContactInfoEvent } from '../model/ClickstreamEvents';
import type { UserZuoraAccountSettingsDto, UserZuoraPaymentMethodDto } from '@zg-rentals/ts-adproducts-client';

const PaymentSettingsPage = () => {
  const { accountSettings, loadingState } = usePaymentMethods();
  const { isMaintenanceMode } = useAppMaintenance();
  const [showPaymentInfoModal, setShowPaymentInfoModal] = React.useState(false);
  const [showBillingInfoModal, setShowBillingInfoModal] = React.useState('');
  const isScreenDesktop = useIsScreenDesktop();
  const history = useHistory();
  const { user } = useRentalManagerUser();

  const paths = useRmwPaths();

  const handleEditClick = (accountNumber: string) => {
    history.push(`${paths.billingPage}/account/${accountNumber}?prev=settings`);
  };

  const handlePayInfo = () => setShowPaymentInfoModal(true);

  const handleBillingInfo = (accountNumber: string) => {
    emitEditBillingContactInfoEvent({ user });
    setShowBillingInfoModal(accountNumber);
  };

  const renderPaymentMethod = (paymentMethod: UserZuoraPaymentMethodDto) => (
    <>
      <Text>{billingUtils.formatPaymentMethod(paymentMethod)}</Text>
      {paymentMethod.paymentType === 'CreditCard' && (
        <>
          <br />
          <Text>{`Expires ${paymentMethod.expirationMonth}/${paymentMethod.expirationYear}`}</Text>
        </>
      )}
    </>
  );

  const renderStatus = (account: UserZuoraAccountSettingsDto) => {
    if (!account.paymentMethodsSynced) {
      return null;
    }
    if (account.hasPaymentMethods) {
      return (
        <svg height="20" width="20" xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="10" cy="10" rx="5" ry="5" style={{ fill: '#17B036' }} />
        </svg>
      );
    }
    return (
      <Icon
        size="md"
        css={{
          color:
            'blue.600' /* oxlint-disable-line constellation/prefer-semantic-tokens -- pre-existing base-token usage; was warn-only under ESLint's pandaLint */,
        }}
      >
        <IconInfoFilled />
      </Icon>
    );
  };

  const renderEditLinkText = (account: UserZuoraAccountSettingsDto) => {
    if (account.paymentMethodsSynced && !account.hasPaymentMethods) {
      return 'Setup now';
    }
    return 'Manage';
  };

  const renderTable = () => (
    <Table.Root aria-label="Account settings" size="sm" appearance="horizontal">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Account</Table.HeaderCell>
          <Table.HeaderCell>Next Payment Date</Table.HeaderCell>
          <Table.HeaderCell>Payment Method</Table.HeaderCell>
          {isScreenDesktop && <Table.HeaderCell>Autopay</Table.HeaderCell>}
          <Table.HeaderCell> </Table.HeaderCell>
          <Table.HeaderCell>Billing Info</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {accountSettings.map((account) => (
          <Table.Row key={account.accountNumber}>
            <Table.Cell>{renderStatus(account)}</Table.Cell>
            <Table.HeaderCell>
              <Heading level={6}>{account.accountName}</Heading>
              <Paragraph textStyle="body-sm">{account.accountNumber}</Paragraph>
            </Table.HeaderCell>
            <Table.Cell>
              {account.autopay ? billingUtils.getNextAutoPaymentDate() : '-'}
              {!isScreenDesktop && (
                <>
                  <Divider />
                  Autopay {account.autopay ? 'ON' : 'OFF'}
                </>
              )}
            </Table.Cell>
            <Table.Cell>
              {!account.autopayMethod || !account.autopay ? '-' : renderPaymentMethod(account.autopayMethod)}
            </Table.Cell>
            {isScreenDesktop && <Table.Cell>{account.autopay ? 'ON' : 'OFF'}</Table.Cell>}
            <Table.Cell>
              <TextButton disabled={isMaintenanceMode} onClick={() => handleEditClick(account.accountNumber)}>
                {renderEditLinkText(account)}
              </TextButton>
            </Table.Cell>
            <Table.Cell>
              <TextButton
                onClick={() => handleBillingInfo(account.accountNumber)}
                data-eventid={EVENT_IDS.EDIT_BILLING_CONTACT_INFO}
              >
                Edit
              </TextButton>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );

  return (
    <AuthCheckWrapper>
      <PermissionCheck route={INVOICE_PAYMENTS}>
        <HeadWrapper title="Payment Settings" />
        <Box css={{ padding: 'md' }}>
          <Box css={{ marginBottom: 'md' }}>
            <Heading level={4}>Accounts</Heading>
            <Text css={{ marginBottom: 'sm' }}>
              We allow credit cards or ACH for online payments for each account in your company.
            </Text>
            <Paragraph>
              Need to pay with other methods?{' '}
              <TextButton textStyle="body-sm" onClick={handlePayInfo} size="sm" data-eventid={EVENT_IDS.PAY_OFFLINE}>
                See alternative options
              </TextButton>
            </Paragraph>
          </Box>
          {loadingState === LoadingConstants.SUCCESS ? renderTable() : <SpinnerContainer />}
        </Box>
        <>{showPaymentInfoModal && <PaymentInfoModal onClose={() => setShowPaymentInfoModal(false)} />}</>
        <>
          {showBillingInfoModal && (
            <ChangeBillingInfoModal
              accountNumber={showBillingInfoModal}
              isMultiFamily={user.isMultifamilyCustomer}
              onClose={() => setShowBillingInfoModal('')}
            />
          )}
        </>
      </PermissionCheck>
    </AuthCheckWrapper>
  );
};

export default PaymentSettingsPage;
